import { useState } from "react";
import { supabase, assignCompToDeal, saveRentRollRow } from "../../supabase";
import { geocodeAddress } from "../../services/geocoding";
import PlacesAutocompleteInput from "../common/PlacesAutocompleteInput";

// ─── Manual Comp / Rent Roll Entry Form ─────────────────────────────────────
// Single inline form used by all three sub-tabs of Deal Comp Set. The schema
// is mode-specific:
//   - mode="sale"      → writes to global `comps` table + deal_assigned_comps
//   - mode="lease"     → writes to global `comps` table + deal_assigned_comps
//   - mode="rent_roll" → writes to `deal_rent_roll` only (subject-specific)
//
// Addresses are geocoded on save via the existing Google Geocoding service so
// the row can render on the map immediately. If geocoding fails, the row is
// still saved (just won't show on the map until someone re-geocodes later).

const FIELD_LABEL_CLS = "block text-[10px] font-bold uppercase tracking-wider text-[#8a8a8a] mb-1";

function inputCls(darkMode) {
  return darkMode
    ? "w-full text-xs px-3 py-1.5 rounded-md bg-[#242424] border border-[#3a3a3a] text-white placeholder-[#6a6a6a] focus:outline-none focus:border-pp-red"
    : "w-full text-xs px-3 py-1.5 rounded-md bg-white border border-pp-gray text-pp-dark placeholder-[#7a7a7a] focus:outline-none focus:border-pp-red";
}

export default function ManualCompForm({
  open,
  onClose,
  onSaved,                // (savedRow) => void
  dealId,
  dealName,
  dealAddresses = [],     // for rent roll subject_address dropdown
  mode = "sale",          // 'sale' | 'lease' | 'rent_roll'
  createdBy = null,
  darkMode = false,
}) {
  const [form, setForm] = useState(() => initialForm(mode, dealAddresses));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function reset() {
    setForm(initialForm(mode, dealAddresses));
    setErr(null);
  }

  async function handleSave() {
    setErr(null);
    if (mode !== "rent_roll" && !form.address?.trim()) { setErr("Address is required."); return; }
    if (mode === "rent_roll" && !form.tenant?.trim()) { setErr("Tenant is required."); return; }
    setSaving(true);
    try {
      let saved;
      if (mode === "rent_roll") {
        saved = await saveRentRollRow(dealId, {
          subject_address: form.subject_address || (dealAddresses[0] || ""),
          tenant: form.tenant?.trim(),
          tenant_industry: form.tenant_industry?.trim() || null,
          suite_id: form.suite_id?.trim() || null,
          suite_sf: form.suite_sf ? Number(form.suite_sf) : null,
          lease_rate: form.lease_rate ? Number(form.lease_rate) : null,
          lease_type: form.lease_type || null,
          lease_start: form.lease_start || null,
          lease_end: form.lease_end || null,
          walt_years: form.walt_years ? Number(form.walt_years) : null,
          notes: form.notes?.trim() || null,
          source: "manual",
          created_by: createdBy,
        });
        if (!saved) throw new Error("Failed to save rent roll row.");
      } else {
        // Geocode address on save so the comp renders on the map immediately.
        const coords = await geocodeAddress(form.address.trim()).catch(() => null);
        // Only include columns that currently exist on the `comps` table —
        // see CompExtractionStaging for the same constraint. from_deal_id
        // isn't in the schema today.
        const payload = {
          type: mode,                                 // 'sale' or 'lease'
          source: "manual_deal_entry",
          address: form.address.trim(),
          city: form.city?.trim() || null,
          state: form.state?.trim() || null,
          market: form.market?.trim() || null,
          property_name: form.property_name?.trim() || null,
          comp_date: form.comp_date || null,
          notes: form.notes?.trim() || null,
          latitude: coords?.lat || null,
          longitude: coords?.lng || null,
          added_by: createdBy || "Manual Deal Entry",
        };
        if (mode === "sale") {
          payload.buyer = form.buyer?.trim() || null;
          payload.seller = form.seller?.trim() || null;
          payload.sale_price = form.sale_price ? Number(form.sale_price) : null;
          payload.building_sf = form.building_sf ? Number(form.building_sf) : null;
          payload.price_psf = form.price_psf ? Number(form.price_psf) : null;
          payload.cap_rate = form.cap_rate ? Number(form.cap_rate) : null;
        } else { // lease
          payload.tenant = form.tenant?.trim() || null;
          payload.suite_sf = form.suite_sf ? Number(form.suite_sf) : null;
          payload.lease_rate = form.lease_rate ? Number(form.lease_rate) : null;
          payload.lease_type = form.lease_type || null;
        }
        const { data, error } = await supabase.from("comps").insert([payload]).select().single();
        if (error) throw error;
        await assignCompToDeal(dealId, data.id, "manual_form", null, createdBy);
        saved = data;
      }
      onSaved?.(saved);
      reset();
      onClose?.();
    } catch (e) {
      console.error("ManualCompForm save:", e);
      setErr(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  const cls = {
    bg: darkMode ? "bg-[#242424]" : "bg-white",
    text: darkMode ? "text-white" : "text-pp-dark",
    muted: darkMode ? "text-[#8a8a8a]" : "text-[#8a8a8a]",
    border: darkMode ? "border-[#3a3a3a]" : "border-pp-gray",
  };

  const title = mode === "sale" ? "Add Sale Comp" : mode === "lease" ? "Add Lease Comp" : "Add Rent Roll Lease";
  const input = inputCls(darkMode);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose}>
      <div className={`${cls.bg} ${cls.text} w-full max-w-2xl max-h-[85vh] rounded-xl shadow-2xl flex flex-col font-montserrat`} onClick={e => e.stopPropagation()}>
        <div className={`px-5 py-4 border-b ${cls.border} flex items-center justify-between flex-shrink-0`}>
          <div>
            <h2 className="text-lg font-bold">{title}</h2>
            <p className={`text-xs ${cls.muted} mt-0.5`}>{dealName ? `→ ${dealName}` : ""}</p>
          </div>
          <button onClick={onClose} className={`text-xl ${cls.muted} hover:${cls.text} px-2`}>✕</button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          {err && (
            <div className="text-xs text-[#a5241b] bg-[rgba(233,53,41,0.07)] border border-[rgba(233,53,41,0.25)] rounded-md px-3 py-2">{err}</div>
          )}

          {/* ── Address block (sale + lease only) ── */}
          {mode !== "rent_roll" && (
            <>
              <div>
                <label className={FIELD_LABEL_CLS}>Address *</label>
                <PlacesAutocompleteInput
                  value={form.address}
                  onChange={(v) => set("address", v)}
                  onSelect={({ address, place }) => {
                    set("address", address);
                    // Best-effort autofill of city/state from Places components
                    const comp = place?.address_components || [];
                    const find = (t) => comp.find(c => c.types.includes(t))?.short_name || "";
                    const city = find("locality") || find("sublocality") || find("administrative_area_level_2");
                    const state = find("administrative_area_level_1");
                    if (city && !form.city) set("city", city);
                    if (state && !form.state) set("state", state);
                  }}
                  placeholder="1234 Main St, City, ST 12345"
                  className={input}
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLS}>City</label>
                  <input type="text" value={form.city} onChange={e => set("city", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>State</label>
                  <input type="text" value={form.state} onChange={e => set("state", e.target.value)} className={input} maxLength={2} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Market</label>
                  <input type="text" value={form.market} onChange={e => set("market", e.target.value)} placeholder="DFW, Miami…" className={input} />
                </div>
              </div>
              <div>
                <label className={FIELD_LABEL_CLS}>Property Name</label>
                <input type="text" value={form.property_name} onChange={e => set("property_name", e.target.value)} placeholder="Optional" className={input} />
              </div>
            </>
          )}

          {/* ── Rent roll subject_address dropdown ── */}
          {mode === "rent_roll" && (
            <div>
              <label className={FIELD_LABEL_CLS}>Subject Property</label>
              {dealAddresses.length <= 1 ? (
                <input type="text" value={dealAddresses[0] || ""} disabled className={`${input} opacity-70`} />
              ) : (
                <select value={form.subject_address} onChange={e => set("subject_address", e.target.value)} className={input}>
                  {dealAddresses.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              )}
            </div>
          )}

          {/* ── Sale-specific ── */}
          {mode === "sale" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLS}>Buyer</label>
                  <input type="text" value={form.buyer} onChange={e => set("buyer", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Seller</label>
                  <input type="text" value={form.seller} onChange={e => set("seller", e.target.value)} className={input} />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLS}>Building SF</label>
                  <input type="number" value={form.building_sf} onChange={e => set("building_sf", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Sale Price ($)</label>
                  <input type="number" value={form.sale_price} onChange={e => set("sale_price", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>$/SF</label>
                  <input type="number" step="0.01" value={form.price_psf} onChange={e => set("price_psf", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Cap Rate (%)</label>
                  <input type="number" step="0.01" value={form.cap_rate} onChange={e => set("cap_rate", e.target.value)} className={input} />
                </div>
              </div>
              <div>
                <label className={FIELD_LABEL_CLS}>Sale Date</label>
                <input type="date" value={form.comp_date} onChange={e => set("comp_date", e.target.value)} className={input + " w-48"} />
              </div>
            </>
          )}

          {/* ── Lease-specific (lease comp) ── */}
          {mode === "lease" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLS}>Tenant</label>
                  <input type="text" value={form.tenant} onChange={e => set("tenant", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Tenant Industry</label>
                  <input type="text" value={form.tenant_industry} onChange={e => set("tenant_industry", e.target.value)} className={input} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLS}>Suite SF</label>
                  <input type="number" value={form.suite_sf} onChange={e => set("suite_sf", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Lease Rate ($/SF)</label>
                  <input type="number" step="0.01" value={form.lease_rate} onChange={e => set("lease_rate", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Lease Type</label>
                  <select value={form.lease_type} onChange={e => set("lease_type", e.target.value)} className={input}>
                    <option value="">—</option>
                    <option value="NNN">NNN</option>
                    <option value="Gross">Gross</option>
                    <option value="Modified Gross">Modified Gross</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={FIELD_LABEL_CLS}>Lease Date</label>
                <input type="date" value={form.comp_date} onChange={e => set("comp_date", e.target.value)} className={input + " w-48"} />
              </div>
            </>
          )}

          {/* ── Rent roll-specific ── */}
          {mode === "rent_roll" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLS}>Tenant *</label>
                  <input type="text" value={form.tenant} onChange={e => set("tenant", e.target.value)} className={input} autoFocus />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Tenant Industry</label>
                  <input type="text" value={form.tenant_industry} onChange={e => set("tenant_industry", e.target.value)} className={input} />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLS}>Suite</label>
                  <input type="text" value={form.suite_id} onChange={e => set("suite_id", e.target.value)} className={input} placeholder="Ste 100" />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Suite SF</label>
                  <input type="number" value={form.suite_sf} onChange={e => set("suite_sf", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Rate ($/SF)</label>
                  <input type="number" step="0.01" value={form.lease_rate} onChange={e => set("lease_rate", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Lease Type</label>
                  <select value={form.lease_type} onChange={e => set("lease_type", e.target.value)} className={input}>
                    <option value="">—</option>
                    <option value="NNN">NNN</option>
                    <option value="Gross">Gross</option>
                    <option value="Modified Gross">Modified Gross</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLS}>Lease Start</label>
                  <input type="date" value={form.lease_start} onChange={e => set("lease_start", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Lease End</label>
                  <input type="date" value={form.lease_end} onChange={e => set("lease_end", e.target.value)} className={input} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>WALT (years)</label>
                  <input type="number" step="0.1" value={form.walt_years} onChange={e => set("walt_years", e.target.value)} className={input} />
                </div>
              </div>
            </>
          )}

          {/* Notes */}
          <div>
            <label className={FIELD_LABEL_CLS}>Notes</label>
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={3} className={input + " resize-none"} />
          </div>
        </div>

        <div className={`px-5 py-3 border-t ${cls.border} flex items-center justify-end gap-2 flex-shrink-0`}>
          <button onClick={onClose} className={`text-xs px-4 py-2 rounded-lg border ${cls.border} ${cls.muted} hover:${cls.text}`}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ color: "#ffffff" }}
            className="text-xs px-4 py-2 rounded-lg bg-pp-red hover:bg-pp-red-light font-semibold disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function initialForm(mode, dealAddresses) {
  if (mode === "rent_roll") {
    return {
      subject_address: dealAddresses[0] || "",
      tenant: "", tenant_industry: "", suite_id: "", suite_sf: "",
      lease_rate: "", lease_type: "", lease_start: "", lease_end: "",
      walt_years: "", notes: "",
    };
  }
  return {
    address: "", city: "", state: "", market: "", property_name: "",
    comp_date: "", notes: "",
    // sale-specific
    buyer: "", seller: "", sale_price: "", building_sf: "", price_psf: "", cap_rate: "",
    // lease-specific
    tenant: "", tenant_industry: "", suite_sf: "", lease_rate: "", lease_type: "",
  };
}
